import React, { useState, useEffect } from 'react';
import { roomsService, superAdminService, bookingsService } from '../services/api';
import { toast } from 'react-toastify';
import { CounterInput } from './ui';
import { format, subDays } from 'date-fns';
import { useIsMobile } from '../hooks';
import { Shield as ShieldIcon } from 'lucide-react';

function StaffReservationSidebar({ isOpen, open, onClose, onReservationCreated, user }) {
  const resolvedIsOpen = typeof isOpen === 'boolean' ? isOpen : open;
  const isMobile = useIsMobile();
  // Check if user is super admin
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  
  // State to toggle between regular and past date booking
  const [isPastDateMode, setIsPastDateMode] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [step, setStep] = useState(1);

  const [reservationData, setReservationData] = useState({
    checkIn: '',
    checkInTime: '13:00',
    checkOut: '',
    checkOutTime: '10:00',
    guestDetails: {
      name: '',
      email: '',
      phone: '',
      gender: 'UNSPECIFIED'
    },
    selectedRoom: null,
    selectedBed: null,
    roomType: 'private',
    courseCount: 1,
    guestCount: 1,
    standardPrice: 0,
    customPrice: '',
    useCustomPrice: false,
    notes: '',
    adminNote: '', // For past date bookings
    reason: '', // For past date bookings
    paymentStatus: 'PENDING'
  });

  useEffect(() => {
    if (resolvedIsOpen) {
      loadRooms();
      setDefaultDates();
    }
  }, [resolvedIsOpen]);

  useEffect(() => {
    if (reservationData.selectedRoom && reservationData.roomType === 'shared') {
      setAvailableBeds(reservationData.selectedRoom.beds || []);
    }
  }, [reservationData.selectedRoom, reservationData.roomType]);

  // Reset dates when past date mode changes
  useEffect(() => {
    if (resolvedIsOpen) {
      setDefaultDates();
    }
  }, [isPastDateMode, resolvedIsOpen]);

  const setDefaultDates = () => {
    const today = new Date();
    let defaultCheckIn, defaultCheckOut;
    
    if (isPastDateMode && isSuperAdmin) {
      // For past date mode, don't set default dates - let user choose
      defaultCheckIn = '';
      defaultCheckOut = '';
    } else {
      // Regular mode - set today and tomorrow
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      defaultCheckIn = today.toISOString().split('T')[0];
      defaultCheckOut = tomorrow.toISOString().split('T')[0];
    }
    
    setReservationData(prev => ({
      ...prev,
      checkIn: defaultCheckIn,
      checkOut: defaultCheckOut
    }));
  };

  const loadRooms = async () => {
    try {
      const response = await roomsService.getAll();
      const roomsData = response.data?.rooms || response.rooms || [];
      setRooms(roomsData);
    } catch (error) {
      console.error('Error loading rooms:', error);
      toast.error('Failed to load rooms');
    }
  };

  const calculateStandardPrice = () => {
    if (!reservationData.selectedRoom || !reservationData.checkIn || !reservationData.checkOut) {
      return 0;
    }

    const checkIn = new Date(reservationData.checkIn);
    const checkOut = new Date(reservationData.checkOut);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    const rate = reservationData.selectedRoom.roomType?.defaultRate || 0;
    
    return rate * nights;
  };

  useEffect(() => {
    const standardPrice = calculateStandardPrice();
    setReservationData(prev => ({
      ...prev,
      standardPrice
    }));
  }, [reservationData.selectedRoom, reservationData.checkIn, reservationData.checkOut]);

  const handleNext = () => {
    if (validateCurrentStep()) {
      setStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    switch (step) {
      case 1:
        if (!reservationData.checkIn || !reservationData.checkOut) {
          toast.error('Please select check-in and check-out dates');
          return false;
        }
        if (new Date(reservationData.checkIn) >= new Date(reservationData.checkOut)) {
          toast.error('Check-out must be after check-in');
          return false;
        }
        return true;
        
      case 2:
        const { name, email, phone } = reservationData.guestDetails;
        if (!name.trim() || !email.trim() || !phone.trim()) {
          toast.error('Please fill in all guest details');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
        
      case 3:
        if (!reservationData.selectedRoom) {
          toast.error('Please select a room');
          return false;
        }
        if (reservationData.roomType === 'shared' && !reservationData.selectedBed) {
          toast.error('Please select a bed');
          return false;
        }
        return true;
        
      case 4:
        return true;
        
      default:
        return true;
    }
  };

  const handleCreateReservation = async () => {
    try {
      setLoading(true);
      
      const checkInDateTime = new Date(`${reservationData.checkIn}T${reservationData.checkInTime}`);
      const checkOutDateTime = new Date(`${reservationData.checkOut}T${reservationData.checkOutTime}`);
      
      const payload = {
        guestDetails: reservationData.guestDetails,
        roomId: reservationData.roomType === 'private' ? reservationData.selectedRoom.id : null,
        checkIn: checkInDateTime.toISOString(),
        checkOut: checkOutDateTime.toISOString(),
        courseCount: reservationData.courseCount || 1,
        guestCount: reservationData.guestCount || 1,
        customPrice: reservationData.useCustomPrice ? parseFloat(reservationData.customPrice) : null,
        notes: reservationData.notes.trim() || null,
        paymentStatus: reservationData.paymentStatus
      };

      // Only include bedId for shared room bookings
      if (reservationData.roomType === 'shared' && reservationData.selectedBed?.id) {
        payload.bedId = reservationData.selectedBed.id;
      }

      let response;
      
      if (isPastDateMode && isSuperAdmin) {
        // Use super admin past date booking endpoint
        if (!reservationData.reason?.trim()) {
          toast.error('Please provide a reason for creating past date booking');
          setLoading(false);
          return;
        }
        
        payload.adminNote = reservationData.adminNote?.trim() || '';
        payload.reason = reservationData.reason.trim();
        
        response = await superAdminService.createPastDateBooking(payload);
      } else {
        // Use regular staff reservation endpoint
        response = await bookingsService.createStaffReservation(payload);
      }

      const bookingData = response.data || response;
      
      toast.success(`${isPastDateMode ? 'Past date booking' : 'Reservation'} created successfully!`);
      onReservationCreated?.(bookingData.booking || bookingData);
      onClose();
      resetForm();
      
    } catch (error) {
      console.error('Error creating reservation:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied: Super admin privileges required for past date bookings');
      } else if (error.response?.status === 409) {
        toast.error('Booking conflict: Selected accommodation is not available for the specified dates');
      } else {
        toast.error(error.message || 'Failed to create reservation');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setReservationData({
      checkIn: '',
      checkInTime: '13:00',
      checkOut: '',
      checkOutTime: '10:00',
      guestDetails: {
        name: '',
        email: '',
        phone: '',
        gender: 'UNSPECIFIED'
      },
      selectedRoom: null,
      selectedBed: null,
      roomType: 'private',
      courseCount: 1,
      guestCount: 1,
      standardPrice: 0,
      customPrice: '',
      useCustomPrice: false,
      notes: '',
      adminNote: '',
      reason: '',
      paymentStatus: 'PENDING'
    });
    // Reset past date mode when closing
    setIsPastDateMode(false);
  };

  // Helper functions for date constraints
  const getMinDate = () => {
    if (isPastDateMode && isSuperAdmin) {
      // Allow up to 2 years in the past for super admin
      return format(subDays(new Date(), 730), 'yyyy-MM-dd');
    } else {
      // Regular mode - today onwards
      return format(new Date(), 'yyyy-MM-dd');
    }
  };

  const getMaxDate = () => {
    if (isPastDateMode && isSuperAdmin) {
      // Past date mode - up to today
      return format(new Date(), 'yyyy-MM-dd');
    } else {
      // Regular mode - no max limit (future dates)
      return null;
    }
  };

  const getMinCheckOutDate = () => {
    if (!reservationData.checkIn) return getMinDate();
    const checkIn = new Date(reservationData.checkIn);
    const nextDay = new Date(checkIn);
    nextDay.setDate(nextDay.getDate() + 1);
    return format(nextDay, 'yyyy-MM-dd');
  };

  const renderTimelineStep = () => (
    <div style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#24332A' }}>
        Booking Timeline
      </h3>
      
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
          Check-in Date & Time
        </label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <input
            type="date"
            value={reservationData.checkIn}
            onChange={(e) => setReservationData(prev => ({ ...prev, checkIn: e.target.value }))}
            min={getMinDate()}
            max={getMaxDate()}
            style={{
              flex: 1,
              padding: '10px',
              fontSize: '14px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px'
            }}
          />
          <input
            type="time"
            value={reservationData.checkInTime}
            onChange={(e) => setReservationData(prev => ({ ...prev, checkInTime: e.target.value }))}
            style={{
              width: '120px',
              padding: '10px',
              fontSize: '14px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px'
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
          Check-out Date & Time
        </label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <input
            type="date"
            value={reservationData.checkOut}
            onChange={(e) => setReservationData(prev => ({ ...prev, checkOut: e.target.value }))}
            min={getMinCheckOutDate()}
            max={getMaxDate()}
            style={{
              flex: 1,
              padding: '10px',
              fontSize: '14px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px'
            }}
          />
          <input
            type="time"
            value={reservationData.checkOutTime}
            onChange={(e) => setReservationData(prev => ({ ...prev, checkOutTime: e.target.value }))}
            style={{
              width: '120px',
              padding: '10px',
              fontSize: '14px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px'
            }}
          />
        </div>
      </div>

      {reservationData.checkIn && reservationData.checkOut && (
        <div style={{
          backgroundColor: '#F0F3EE',
          padding: '15px',
          borderRadius: '8px',
          border: '1px solid #1A3E2A'
        }}>
          <div style={{ fontSize: '14px', color: '#122E20', fontWeight: '600' }}>
            Duration: {Math.ceil((new Date(reservationData.checkOut) - new Date(reservationData.checkIn)) / (1000 * 60 * 60 * 24))} nights
          </div>
        </div>
      )}
    </div>
  );

  const renderGuestDetailsStep = () => (
    <div style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#24332A' }}>
        Guest Details
      </h3>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
          Full Name *
        </label>
        <input
          type="text"
          value={reservationData.guestDetails.name}
          onChange={(e) => setReservationData(prev => ({
            ...prev,
            guestDetails: { ...prev.guestDetails, name: e.target.value }
          }))}
          placeholder="Enter guest's full name"
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #D2CDBF',
            borderRadius: '6px'
          }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
          Email Address *
        </label>
        <input
          type="email"
          value={reservationData.guestDetails.email}
          onChange={(e) => setReservationData(prev => ({
            ...prev,
            guestDetails: { ...prev.guestDetails, email: e.target.value }
          }))}
          placeholder="guest@example.com"
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #D2CDBF',
            borderRadius: '6px'
          }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
          Phone Number *
        </label>
        <input
          type="tel"
          value={reservationData.guestDetails.phone}
          onChange={(e) => setReservationData(prev => ({
            ...prev,
            guestDetails: { ...prev.guestDetails, phone: e.target.value }
          }))}
          placeholder="+1 (555) 123-4567"
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #D2CDBF',
            borderRadius: '6px'
          }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
          Gender
        </label>
        <select
          value={reservationData.guestDetails.gender}
          onChange={(e) => setReservationData(prev => ({
            ...prev,
            guestDetails: { ...prev.guestDetails, gender: e.target.value }
          }))}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #D2CDBF',
            borderRadius: '6px'
          }}
        >
          <option value="UNSPECIFIED">Prefer not to say</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      
      {/* Guest Count and Course Count Fields */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '15px', 
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#F5F3EB',
        borderRadius: '8px',
        border: '1px solid #E3DFD3'
      }}>
        <CounterInput
          label="Guest Count"
          value={reservationData.guestCount || 1}
          onChange={(value) => setReservationData(prev => ({ ...prev, guestCount: value }))}
          min={1}
          max={10}
          helperText="Number of guests"
          style={{ width: '100%' }}
        />
        
        <CounterInput
          label="Course Count"
          value={reservationData.courseCount || 1}
          onChange={(value) => setReservationData(prev => ({ ...prev, courseCount: value }))}
          min={1}
          max={5}
          helperText="Course enrollments"
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  const renderRoomSelectionStep = () => {
    const filteredRooms = rooms.filter(room => 
      reservationData.roomType === 'private' 
        ? !room.roomType?.isShared 
        : room.roomType?.isShared
    );

    return (
      <div style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#24332A' }}>
          Room Selection
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
            Room Type
          </label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <button
              onClick={() => setReservationData(prev => ({ 
                ...prev, 
                roomType: 'private', 
                selectedRoom: null, 
                selectedBed: null 
              }))}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                border: '2px solid',
                borderColor: reservationData.roomType === 'private' ? '#1A3E2A' : '#D2CDBF',
                backgroundColor: reservationData.roomType === 'private' ? '#E1E9E2' : 'white',
                color: reservationData.roomType === 'private' ? '#1A3E2A' : '#5F6B61',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Private Room
            </button>
            <button
              onClick={() => setReservationData(prev => ({ 
                ...prev, 
                roomType: 'shared', 
                selectedRoom: null, 
                selectedBed: null 
              }))}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                border: '2px solid',
                borderColor: reservationData.roomType === 'shared' ? '#1A3E2A' : '#D2CDBF',
                backgroundColor: reservationData.roomType === 'shared' ? '#E1E9E2' : 'white',
                color: reservationData.roomType === 'shared' ? '#1A3E2A' : '#5F6B61',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Shared Room
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
            Available Rooms
          </label>
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #E3DFD3', borderRadius: '8px' }}>
            {filteredRooms.map((room) => (
              <div
                key={room.id}
                onClick={() => setReservationData(prev => ({ 
                  ...prev, 
                  selectedRoom: room, 
                  selectedBed: null 
                }))}
                style={{
                  padding: '15px',
                  borderBottom: '1px solid #EEEBE1',
                  cursor: 'pointer',
                  backgroundColor: reservationData.selectedRoom?.id === room.id ? '#F0F3EE' : 'white',
                  borderLeft: reservationData.selectedRoom?.id === room.id ? '4px solid #1A3E2A' : '4px solid transparent'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#24332A' }}>
                      Room {room.roomNumber}
                    </div>
                    <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                      {room.roomType?.name} • {room.roomType?.isShared ? `${room.beds?.length || 0} beds` : 'Private'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#163626' }}>
                      ₹{room.roomType?.defaultRate || 0}
                    </div>
                    <div style={{ fontSize: '11px', color: '#5F6B61' }}>
                      per night
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {reservationData.roomType === 'shared' && reservationData.selectedRoom && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
              Select Bed
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
              {availableBeds.map((bed) => (
                <button
                  key={bed.id}
                  onClick={() => setReservationData(prev => ({ ...prev, selectedBed: bed }))}
                  style={{
                    padding: '15px 10px',
                    fontSize: '12px',
                    fontWeight: '600',
                    border: '2px solid',
                    borderColor: reservationData.selectedBed?.id === bed.id ? '#1A3E2A' : '#D2CDBF',
                    backgroundColor: reservationData.selectedBed?.id === bed.id ? '#E1E9E2' : 'white',
                    color: reservationData.selectedBed?.id === bed.id ? '#1A3E2A' : '#5F6B61',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  <div>Bed {bed.bedNumber}</div>
                  {bed.isFemaleOnly && (
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#A9613C', 
                      marginTop: '4px',
                      fontWeight: '700'
                    }}>
                      Female Only
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {reservationData.selectedRoom && (
          <div style={{
            backgroundColor: '#F0F3EE',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #1A3E2A'
          }}>
            <div style={{ fontSize: '14px', color: '#163626', fontWeight: '600' }}>
              Selection: Room {reservationData.selectedRoom.roomNumber}
              {reservationData.selectedBed && ` - Bed ${reservationData.selectedBed.bedNumber}`}
            </div>
            <div style={{ fontSize: '12px', color: '#0F2619', marginTop: '4px' }}>
              {reservationData.selectedRoom.roomType?.name} • ₹{reservationData.selectedRoom.roomType?.defaultRate}/night
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPricingStep = () => (
    <div style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#24332A' }}>
        Pricing & Payment
      </h3>
      
      <div style={{
        backgroundColor: '#F5F3EB',
        padding: '15px',
        borderRadius: '8px',
        border: '1px solid #E3DFD3',
        marginBottom: '20px'
      }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
          Standard Pricing
        </h4>
        <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '8px' }}>
          {reservationData.selectedRoom?.roomType?.name} • {Math.ceil((new Date(reservationData.checkOut) - new Date(reservationData.checkIn)) / (1000 * 60 * 60 * 24))} nights
        </div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#163626' }}>
          ₹{reservationData.standardPrice.toLocaleString()}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <input
            type="checkbox"
            checked={reservationData.useCustomPrice}
            onChange={(e) => setReservationData(prev => ({ 
              ...prev, 
              useCustomPrice: e.target.checked,
              customPrice: e.target.checked ? prev.standardPrice.toString() : ''
            }))}
            style={{ width: '16px', height: '16px' }}
          />
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
            Use Custom Price (Negotiated Rate)
          </span>
        </label>
        
        {reservationData.useCustomPrice && (
          <div>
            <input
              type="number"
              value={reservationData.customPrice}
              onChange={(e) => setReservationData(prev => ({ ...prev, customPrice: e.target.value }))}
              placeholder="Enter custom price"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                fontWeight: '600',
                border: '2px solid #1A3E2A',
                borderRadius: '8px',
                color: '#24332A'
              }}
            />
            <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '6px' }}>
              Staff can negotiate and adjust the price based on special circumstances
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
          Payment Status
        </label>
        <select
          value={reservationData.paymentStatus}
          onChange={(e) => setReservationData(prev => ({ ...prev, paymentStatus: e.target.value }))}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #D2CDBF',
            borderRadius: '6px'
          }}
        >
          <option value="PENDING">Pending Payment</option>
          <option value="SUCCESS">Paid</option>
          <option value="FAILED">Payment Failed</option>
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
          Booking Notes (Optional)
        </label>
        <textarea
          value={reservationData.notes}
          onChange={(e) => setReservationData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Add any special notes or requirements..."
          rows={4}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #D2CDBF',
            borderRadius: '6px',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Past Date Booking Fields - Only show when in past date mode */}
      {isPastDateMode && isSuperAdmin && (
        <div style={{ 
          padding: '16px',
          backgroundColor: '#F9EEEB',
          border: '2px solid #D48A7C',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#8E3628', fontSize: '14px', fontWeight: '700' }}>
            Past Date Booking Information
          </h4>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
              Reason for Past Date Booking *
            </label>
            <textarea
              value={reservationData.reason}
              onChange={(e) => setReservationData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Explain why this past date booking is being created (required)"
              rows={2}
              required
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: reservationData.reason.trim() ? '1px solid #D2CDBF' : '2px solid #A13F2F',
                borderRadius: '6px',
                resize: 'vertical'
              }}
            />
            <small style={{ color: '#481B14', fontSize: '12px' }}>
              Required: Provide a clear reason for creating this past date booking
            </small>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
              Admin Note (Optional)
            </label>
            <textarea
              value={reservationData.adminNote}
              onChange={(e) => setReservationData(prev => ({ ...prev, adminNote: e.target.value }))}
              placeholder="Internal admin notes for this booking"
              rows={2}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                resize: 'vertical'
              }}
            />
          </div>
        </div>
      )}

      <div style={{
        backgroundColor: '#F0F3EE',
        padding: '20px',
        borderRadius: '8px',
        border: '2px solid #1A3E2A'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', gap: '10px' }}>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
            Total Amount:
          </span>
          <span style={{ fontSize: '24px', fontWeight: '800', color: '#1A3E2A' }}>
            ₹{(reservationData.useCustomPrice ? parseFloat(reservationData.customPrice || 0) : reservationData.standardPrice).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );

  const renderConfirmationStep = () => (
    <div style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#24332A' }}>
        Confirmation
      </h3>
      
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
          Booking Details
        </h4>
        <div style={{ fontSize: '14px', color: '#24332A', lineHeight: '1.6' }}>
          <div><strong>Check-in:</strong> {new Date(`${reservationData.checkIn}T${reservationData.checkInTime}`).toLocaleString()}</div>
          <div><strong>Check-out:</strong> {new Date(`${reservationData.checkOut}T${reservationData.checkOutTime}`).toLocaleString()}</div>
          <div><strong>Duration:</strong> {Math.ceil((new Date(reservationData.checkOut) - new Date(reservationData.checkIn)) / (1000 * 60 * 60 * 24))} nights</div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
          Guest Information
        </h4>
        <div style={{ fontSize: '14px', color: '#24332A', lineHeight: '1.6' }}>
          <div><strong>Name:</strong> {reservationData.guestDetails.name}</div>
          <div><strong>Email:</strong> {reservationData.guestDetails.email}</div>
          <div><strong>Phone:</strong> {reservationData.guestDetails.phone}</div>
          <div><strong>Gender:</strong> {reservationData.guestDetails.gender}</div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
          Accommodation
        </h4>
        <div style={{ fontSize: '14px', color: '#24332A', lineHeight: '1.6' }}>
          <div><strong>Room:</strong> {reservationData.selectedRoom?.roomNumber}</div>
          <div><strong>Type:</strong> {reservationData.selectedRoom?.roomType?.name}</div>
          {reservationData.selectedBed && (
            <div><strong>Bed:</strong> {reservationData.selectedBed.bedNumber}</div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
          Payment Details
        </h4>
        <div style={{ fontSize: '14px', color: '#24332A', lineHeight: '1.6' }}>
          <div><strong>Amount:</strong> ₹{(reservationData.useCustomPrice ? parseFloat(reservationData.customPrice || 0) : reservationData.standardPrice).toLocaleString()}</div>
          <div><strong>Payment Status:</strong> {reservationData.paymentStatus}</div>
          {reservationData.useCustomPrice && (
            <div style={{ color: '#9E712E', fontStyle: 'italic' }}>
              * Custom negotiated rate applied
            </div>
          )}
        </div>
      </div>

      {reservationData.notes && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '10px' }}>
            Notes
          </h4>
          <div style={{ 
            fontSize: '14px', 
            color: '#24332A', 
            backgroundColor: '#F5F3EB',
            padding: '10px',
            borderRadius: '6px',
            fontStyle: 'italic'
          }}>
            {reservationData.notes}
          </div>
        </div>
      )}

      <div style={{
        backgroundColor: '#F0F3EE',
        padding: '15px',
        borderRadius: '8px',
        border: '1px solid #1A3E2A',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#163626' }}>
          Ready to create this reservation?
        </div>
        <div style={{ fontSize: '12px', color: '#0F2619', marginTop: '4px' }}>
          This booking will be automatically confirmed as a staff reservation.
        </div>
      </div>
    </div>
  );

  if (!resolvedIsOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: isMobile ? '100vw' : '500px',
      maxWidth: '100vw',
      height: '100vh',
      backgroundColor: 'white',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      transform: resolvedIsOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease-in-out'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isMobile ? '16px' : '20px',
        borderBottom: '2px solid #EEEBE1',
        backgroundColor: '#F5F3EB'
      }}>
        <div>
          <h2 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: '800', color: '#24332A' }}>
            {isPastDateMode ? 'Create Past Date Booking' : 'Create Reservation'}
          </h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#5F6B61' }}>
            Step {step} of 5{isPastDateMode ? ' (Past Date Mode)' : ''}
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#5F6B61',
            padding: '5px'
          }}
        >
          ×
        </button>
      </div>

      {/* Super Admin Past Date Toggle */}
      {isSuperAdmin && (
        <div style={{ 
          padding: '16px 20px', 
          backgroundColor: '#F3E6CB', 
          borderBottom: '1px solid #B68235' 
        }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '10px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '16px' }}><ShieldIcon size="1em" aria-hidden="true" /></span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F' }}>Super Admin Mode</span>
              </div>
              <p style={{ margin: 0, fontSize: '12px', color: '#4F3818' }}>
                {isPastDateMode ? 'Creating past date booking' : 'Creating regular booking'}
              </p>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#684A1F' }}>
                Past Date
              </span>
              <div style={{ 
                position: 'relative',
                width: '50px',
                height: '24px',
                backgroundColor: isPastDateMode ? '#8E3628' : '#5F6B61',
                borderRadius: '12px',
                transition: 'background-color 0.3s ease',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={isPastDateMode}
                  onChange={(e) => {
                    setIsPastDateMode(e.target.checked);
                    setDefaultDates(); // Reset dates when toggling
                  }}
                  style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                />
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: isPastDateMode ? '28px' : '2px',
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </div>
            </label>
          </div>
        </div>
      )}

      <div style={{ padding: isMobile ? '0 16px 16px 16px' : '0 20px 20px 20px', backgroundColor: '#F5F3EB' }}>
        <div style={{
          width: '100%',
          height: '6px',
          backgroundColor: '#E3DFD3',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${(step / 5) * 100}%`,
            height: '100%',
            backgroundColor: '#1A3E2A',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {step === 1 && renderTimelineStep()}
        {step === 2 && renderGuestDetailsStep()}
        {step === 3 && renderRoomSelectionStep()}
        {step === 4 && renderPricingStep()}
        {step === 5 && renderConfirmationStep()}
      </div>

      <div style={{
        padding: isMobile ? '16px' : '20px',
        borderTop: '1px solid #E3DFD3',
        backgroundColor: '#F5F3EB',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        {step > 1 && (
          <button
            onClick={handleBack}
            style={{
              padding: '12px 20px',
              backgroundColor: '#5F6B61',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
        )}
        
        <div style={{ flex: 1, minWidth: isMobile ? '100%' : 'auto' }} />
        
        {step < 5 ? (
          <button
            onClick={handleNext}
            style={{
              padding: '12px 20px',
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Next →
          </button>
        ) : (
          <button
            onClick={handleCreateReservation}
            disabled={loading || (isPastDateMode && isSuperAdmin && !reservationData.reason?.trim())}
            style={{
              padding: '12px 20px',
              backgroundColor: loading || (isPastDateMode && isSuperAdmin && !reservationData.reason?.trim()) 
                ? '#A5A89B' 
                : (isPastDateMode ? '#8E3628' : '#1A3E2A'),
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading || (isPastDateMode && isSuperAdmin && !reservationData.reason?.trim()) 
                ? 'not-allowed' 
                : 'pointer'
            }}
          >
            {loading 
              ? 'Creating...' 
              : isPastDateMode 
                ? 'Create Past Date Booking'
                : 'Create Reservation'
            }
          </button>
        )}
      </div>
    </div>
  );
}

export default StaffReservationSidebar;